/**
 * Auto-Gift: Complimentary TDS Meter
 * Store: WaterLabs (7q6yfy-p9.myshopify.com)
 *
 * Automatically adds a complimentary TDS Meter when qualifying subtotal >= ₹2000,
 * and removes it when qualifying subtotal < ₹2000.
 * If customer manually removes the gift while eligible, respects their choice.
 * If subtotal subsequently drops below ₹2000, decline state is reset.
 */
;(function () {
  'use strict'

  // ===========================================================================
  // CONFIGURATION
  // ===========================================================================

  var CONFIG = {
    /** Variant ID of the TDS Meter gift product */
    AUTO_GIFT_VARIANT: 45852185133156,

    /** Minimum qualifying subtotal in subunits (paise). ₹2000 = 200000 */
    GIFT_THRESHOLD: 200000,

    /** Hidden line-item property key used to tag auto-added gifts */
    AUTO_GIFT_PROPERTY: '_auto_gift',

    /** Value for the hidden property */
    AUTO_GIFT_VALUE: 'true',

    /** sessionStorage key for tracking customer decline */
    DECLINE_KEY: 'autoGiftDeclined',

    /** Debounce delay in milliseconds */
    DEBOUNCE_MS: 300,

    /** Maximum consecutive add failures before circuit breaker trips */
    MAX_FAILURES: 3,

    /** Enable debug logging (set to false for production) */
    DEBUG: true
  }

  // ===========================================================================
  // STATE
  // ===========================================================================

  var _isProcessing = false
  var _isInternalUpdate = false
  var _debounceTimer = null
  var _consecutiveFailures = 0
  var _manualRemoveClicked = false

  // ===========================================================================
  // LOGGING
  // ===========================================================================

  function log () {
    if (!CONFIG.DEBUG) return
    var args = Array.prototype.slice.call(arguments)
    args.unshift('[AutoGift]')
    console.log.apply(console, args)
  }

  function warn () {
    if (!CONFIG.DEBUG) return
    var args = Array.prototype.slice.call(arguments)
    args.unshift('[AutoGift]')
    console.warn.apply(console, args)
  }

  // ===========================================================================
  // DECLINE MANAGEMENT (sessionStorage)
  // ===========================================================================

  function isDeclined () {
    try {
      return sessionStorage.getItem(CONFIG.DECLINE_KEY) === 'true'
    } catch (e) {
      return false
    }
  }

  function setDeclined () {
    try {
      sessionStorage.setItem(CONFIG.DECLINE_KEY, 'true')
      log('Gift Declined — customer manually removed the complimentary gift')
    } catch (e) {}
  }

  function clearDeclined () {
    try {
      if (sessionStorage.getItem(CONFIG.DECLINE_KEY) === 'true') {
        sessionStorage.removeItem(CONFIG.DECLINE_KEY)
        log('Gift Restored — decline flag reset because subtotal is below threshold')
      }
    } catch (e) {}
  }

  // ===========================================================================
  // CART API HELPERS
  // ===========================================================================

  function fetchCart () {
    var cartUrl = window.Shopify && window.Shopify.routes
      ? window.Shopify.routes.cart_url
      : '/cart'
    return fetch(cartUrl + '.js?_=' + Date.now(), {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (res) {
      if (!res.ok) throw new Error('Failed to fetch cart: ' + res.status)
      return res.json()
    })
  }

  function addGiftToCart () {
    var addUrl = window.Shopify && window.Shopify.routes
      ? window.Shopify.routes.cart_add_url
      : '/cart/add'

    var properties = {}
    properties[CONFIG.AUTO_GIFT_PROPERTY] = CONFIG.AUTO_GIFT_VALUE

    return fetch(addUrl + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        items: [{
          id: CONFIG.AUTO_GIFT_VARIANT,
          quantity: 1,
          properties: properties
        }],
        sections: ['cart-count-badge', 'offcanvas-cart'],
        sections_url: window.location.pathname
      })
    }).then(function (res) {
      if (!res.ok) {
        return res.json().catch(function () { return {} }).then(function (err) {
          throw new Error(err.description || err.message || 'Add failed: ' + res.status)
        })
      }
      return res.json()
    })
  }

  function removeGiftFromCart (lineItemKey) {
    var changeUrl = window.Shopify && window.Shopify.routes
      ? window.Shopify.routes.cart_change_url
      : '/cart/change'

    return fetch(changeUrl + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: lineItemKey,
        quantity: 0,
        sections: ['cart-count-badge', 'offcanvas-cart'],
        sections_url: window.location.pathname
      })
    }).then(function (res) {
      if (!res.ok) {
        return res.json().catch(function () { return {} }).then(function (err) {
          throw new Error(err.description || err.message || 'Remove failed: ' + res.status)
        })
      }
      return res.json()
    })
  }

  // ===========================================================================
  // CART ANALYSIS
  // ===========================================================================

  function isAutoGiftItem (item) {
    return (
      item.variant_id === CONFIG.AUTO_GIFT_VARIANT &&
      item.properties !== null &&
      typeof item.properties === 'object' &&
      item.properties[CONFIG.AUTO_GIFT_PROPERTY] === CONFIG.AUTO_GIFT_VALUE
    )
  }

  function isPurchasedTdsMeter (item) {
    return (
      item.variant_id === CONFIG.AUTO_GIFT_VARIANT &&
      !isAutoGiftItem(item)
    )
  }

  function findAutoGiftKey (cart) {
    for (var i = 0; i < cart.items.length; i++) {
      if (isAutoGiftItem(cart.items[i])) {
        return cart.items[i].key
      }
    }
    return null
  }

  function hasPurchasedTdsMeter (cart) {
    for (var i = 0; i < cart.items.length; i++) {
      if (isPurchasedTdsMeter(cart.items[i])) {
        return true
      }
    }
    return false
  }

  function calculateQualifyingSubtotal (cart) {
    var subtotal = 0
    for (var i = 0; i < cart.items.length; i++) {
      if (!isAutoGiftItem(cart.items[i])) {
        subtotal += cart.items[i].original_line_price
      }
    }
    return subtotal
  }

  // ===========================================================================
  // UI REFRESH
  // ===========================================================================

  function refreshCartUI (sections) {
    if (!sections) return

    var cartContainer = document.querySelector('cart-container')
    if (cartContainer && typeof cartContainer.reloadCartElements === 'function') {
      cartContainer.reloadCartElements(sections)
      log('Cart UI refreshed via reloadCartElements')
    }
  }

  // ===========================================================================
  // EVALUATION LOGIC
  // ===========================================================================

  function evaluateGift () {
    if (_isProcessing || _isInternalUpdate) {
      log('Skipped — evaluation in progress or internal update')
      return
    }
    _isProcessing = true

    fetchCart()
      .then(function (cart) {
        var qualifyingSubtotal = calculateQualifyingSubtotal(cart)
        var autoGiftKey = findAutoGiftKey(cart)
        var hasPurchased = hasPurchasedTdsMeter(cart)
        var isEligible = qualifyingSubtotal >= CONFIG.GIFT_THRESHOLD

        log('Cart Loaded —', cart.items.length, 'items')
        log('Subtotal (qualifying):', (qualifyingSubtotal / 100).toFixed(2))
        log('Eligible (>= 2000):', isEligible)
        log('Auto-gift in cart:', autoGiftKey !== null)
        log('Purchased TDS Meter in cart:', hasPurchased)
        log('Declined state:', isDeclined())

        // ---------------------------------------------------------------------
        // SCENARIO 1: Subtotal is below ₹2000
        // ---------------------------------------------------------------------
        if (!isEligible) {
          // Subtotal dropped below ₹2000 -> Always clear decline flag so if customer
          // re-qualifies later, they get the gift again as a fresh promotion!
          clearDeclined()
          _manualRemoveClicked = false

          if (autoGiftKey !== null) {
            log('Subtotal below ₹2000 -> auto-removing complimentary gift')
            _isInternalUpdate = true
            return removeGiftFromCart(autoGiftKey)
              .then(function (data) {
                _consecutiveFailures = 0
                log('Gift Removed successfully')
                refreshCartUI(data.sections)
              })
              .catch(function (err) {
                warn('Failed to remove gift:', err.message)
              })
              .then(function () {
                _isInternalUpdate = false
              })
          } else {
            log('Subtotal below ₹2000, no auto-gift to remove')
          }
          return
        }

        // ---------------------------------------------------------------------
        // SCENARIO 2: Subtotal is ₹2000 or more
        // ---------------------------------------------------------------------

        // If customer explicitly clicked remove on the auto-gift item
        if (_manualRemoveClicked) {
          _manualRemoveClicked = false
          setDeclined()
          log('Customer manually removed the gift while eligible — decline flag set')
          return
        }

        // If auto-gift is already present, do nothing
        if (autoGiftKey !== null) {
          log('Gift already present in cart — no action needed')
          return
        }

        // Rule 4 & 5: If customer purchased standard TDS Meter(s), don't add another
        if (hasPurchased) {
          log('Customer already has purchased TDS Meter in cart — skipping auto-add')
          return
        }

        // Check if customer explicitly declined in this session
        if (isDeclined()) {
          log('Customer previously declined the gift while eligible — skipping auto-add')
          return
        }

        // Circuit breaker check
        if (_consecutiveFailures >= CONFIG.MAX_FAILURES) {
          warn('Circuit breaker active (' + _consecutiveFailures + ' failures) — skipping auto-add')
          return
        }

        // Add the complimentary gift!
        log('Eligible without gift — auto-adding complimentary TDS Meter')
        _isInternalUpdate = true
        return addGiftToCart()
          .then(function (data) {
            _consecutiveFailures = 0
            log('Gift Added successfully!')
            refreshCartUI(data.sections)
          })
          .catch(function (err) {
            _consecutiveFailures++
            warn('Failed to add gift:', err.message)
          })
          .then(function () {
            _isInternalUpdate = false
          })
      })
      .catch(function (err) {
        warn('Evaluation error:', err.message)
      })
      .then(function () {
        _isProcessing = false
      })
  }

  function debouncedEvaluate () {
    clearTimeout(_debounceTimer)
    _debounceTimer = setTimeout(evaluateGift, CONFIG.DEBOUNCE_MS)
  }

  // ===========================================================================
  // INTERCEPTION & EVENT LISTENERS
  // ===========================================================================

  function initEventListeners () {
    // Intercept manual clicks on remove buttons in the cart drawer
    document.addEventListener('click', function (e) {
      var removeBtn = e.target.closest('.btn-line-item-remove');
      if (removeBtn) {
        var lineItemKey = removeBtn.dataset.lineItemKey || '';
        // If this removed item is the auto-gift item, mark manual remove
        fetchCart().then(function (cart) {
          var autoGiftKey = findAutoGiftKey(cart);
          if (autoGiftKey && (autoGiftKey === lineItemKey || lineItemKey.indexOf(autoGiftKey) !== -1)) {
            _manualRemoveClicked = true;
            log('Manual remove clicked on auto-gift item:', lineItemKey);
          }
        }).catch(function () {});
      }
    }, true);

    // Listen to Jhango theme cart events
    window.addEventListener('kt.cart.added', function () {
      if (_isInternalUpdate) return
      log('Event: kt.cart.added')
      debouncedEvaluate()
    })

    window.addEventListener('kt.cart.changed', function () {
      if (_isInternalUpdate) return
      log('Event: kt.cart.changed')
      debouncedEvaluate()
    })

    window.addEventListener('kt.cart.reloaded', function () {
      if (_isInternalUpdate) return
      log('Event: kt.cart.reloaded')
      debouncedEvaluate()
    })

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') {
        log('Tab became visible — re-evaluating')
        debouncedEvaluate()
      }
    })

    log('Event listeners registered')
  }

  // ===========================================================================
  // INITIALIZATION
  // ===========================================================================

  function init () {
    log('Initializing — Variant:', CONFIG.AUTO_GIFT_VARIANT, '| Threshold: ₹' + (CONFIG.GIFT_THRESHOLD / 100))
    initEventListeners()
    setTimeout(evaluateGift, 500)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
})()

